import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import { useSettings } from "@/context/SettingsContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import TaskList from "@/components/TaskList";
import SentList from "@/components/SentList";
import TaskModal from "@/components/TaskModal";
import QuickGenerate from "@/components/QuickGenerate";
import WelcomeCard from "@/components/WelcomeCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Sprout, Clock, CheckCircle2, Carrot, ArrowUpDown, CalendarClock, Trash2, X, Check, Send, Inbox, SlidersHorizontal, Link2, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { categoryDotCls } from "@/lib/constants";
import { SNOOZE_DEFAULTS, buildSnoozeOptions } from "@/lib/constants";

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const SORT_KEY = "carrot_sort_by_tab";
const FILTER_KEY = "carrot_filters_by_tab";
const DEFAULT_SORT = { inbox: "priority", shared: "priority", snoozed: "timeleft", done: "priority", sent: "newest" };
const SNOOZE_COUNTS_KEY = "carrot_snooze_counts";
const getSnoozeCounts = () => { try { return JSON.parse(localStorage.getItem(SNOOZE_COUNTS_KEY)) || {}; } catch { return {}; } };
const computeFav = (c) => {
  let best = "1h", bestN = -1;
  for (const [k, v] of Object.entries(c)) { if (v > bestN) { bestN = v; best = k; } }
  return best;
};
const toLocalInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
const tomorrow9 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toLocalInput(d);
};

const bucketOf = (t, now) => {
  if (t.share_status === "pending") return "shared";
  if (t.status === "done") return "done";
  if (t.snoozed_until && new Date(t.snoozed_until).getTime() > now) return "snoozed";
  return "inbox";
};

const DONE_WINDOWS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "lastweek", label: "Last week" },
  { key: "month", label: "This month" },
  { key: "all", label: "All" },
];
const startOfToday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x.getTime(); };
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x.getTime(); };
const inDoneWindow = (t, win, now) => {
  if (win === "all") return true;
  if (!t.completed_at) return false;
  const c = new Date(t.completed_at).getTime();
  const nowD = new Date(now);
  if (win === "today") return c >= startOfToday(nowD);
  if (win === "week") return c >= startOfWeek(nowD);
  if (win === "lastweek") { const ws = startOfWeek(nowD); return c >= ws - 7 * 86400000 && c < ws; }
  if (win === "month") return c >= startOfMonth(nowD);
  return true;
};

const SNOOZE_WINDOWS = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This week" },
  { key: "nextweek", label: "Next week" },
  { key: "all", label: "All" },
];
const inSnoozeWindow = (t, win, now) => {
  if (win === "all") return true;
  if (!t.snoozed_until) return false;
  const s = new Date(t.snoozed_until).getTime();
  const nowD = new Date(now);
  const todayStart = startOfToday(nowD);
  const tomorrowStart = todayStart + 86400000;
  const dayAfterStart = todayStart + 2 * 86400000;
  const nextWeekStart = startOfWeek(nowD) + 7 * 86400000;
  const weekAfterStart = startOfWeek(nowD) + 14 * 86400000;
  if (win === "today") return s < tomorrowStart;
  if (win === "tomorrow") return s >= tomorrowStart && s < dayAfterStart;
  if (win === "week") return s < nextWeekStart;
  if (win === "nextweek") return s >= nextWeekStart && s < weekAfterStart;
  return true;
};

export default function Dashboard() {
  const { settings } = useSettings();
  const { activeId } = useWorkspace();
  const { user } = useAuth();
  const userId = user?.user_id;
  const [assignFilter, setAssignFilter] = useState("my");
  const [wsMembers, setWsMembers] = useState([]);
  const snoozeOptions = useMemo(() => buildSnoozeOptions(settings), [settings]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortMap, setSortMap] = useState(() => {
    try { return { ...DEFAULT_SORT, ...(JSON.parse(localStorage.getItem(SORT_KEY)) || {}) }; }
    catch { return { ...DEFAULT_SORT }; }
  });
  const [tab, setTab] = useState("inbox");  const [now, setNow] = useState(Date.now());
  let sortBy = sortMap[tab] || DEFAULT_SORT[tab] || "priority";
  if (sortBy === "timeleft" && tab !== "snoozed") sortBy = "priority";
  const setSortBy = (v) => setSortMap((m) => {
    const next = { ...m, [tab]: v };
    try { localStorage.setItem(SORT_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  const [filterMap, setFilterMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; }
  });
  const categoryFilter = filterMap[tab]?.category || "all";
  const urgencyFilter = filterMap[tab]?.urgency || "all";
  const setFilters = (patch) => setFilterMap((m) => {
    const next = { ...m, [tab]: { category: "all", urgency: "all", ...(m[tab] || {}), ...patch } };
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  const setCategoryFilter = (v) => setFilters({ category: v });
  const setUrgencyFilter = (v) => setFilters({ urgency: v });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [customTarget, setCustomTarget] = useState(null);
  const [customValue, setCustomValue] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareEmailInput, setShareEmailInput] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [shareLinked, setShareLinked] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [favKey, setFavKey] = useState(() => computeFav(getSnoozeCounts()));
  const [sent, setSent] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [doneVisible, setDoneVisible] = useState(50);
  const [doneWindow, setDoneWindow] = useState("today");
  const [snoozeWindow, setSnoozeWindow] = useState("all");
  const [leaveTarget, setLeaveTarget] = useState(null);
  const undoRef = useRef(null);
  const snoozedRef = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tasks${activeId ? `?workspace=${activeId}` : ""}`);
      setTasks(res.data);
    } catch (e) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  // load workspace members (for assignment) when a workspace is active
  useEffect(() => {
    if (!activeId) { setWsMembers([]); return; }
    api.get(`/workspaces/${activeId}/members`).then((r) => setWsMembers(r.data.members)).catch(() => setWsMembers([]));
  }, [activeId]);

  const onAssign = async (task, uid) => {
    const m = wsMembers.find((x) => x.user_id === uid);
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, assignee: uid, assignee_name: m ? m.name : null } : t)));
    try { await api.post(`/tasks/${task.id}/assign`, { user_id: uid }); }
    catch { toast.error("Could not assign"); load(); }
  };

  // Live sync: quietly pick up linked-root changes made by other people
  useEffect(() => {
    const silent = () => api.get(`/tasks${activeId ? `?workspace=${activeId}` : ""}`).then((r) => setTasks(r.data)).catch(() => {});
    const id = setInterval(silent, 25000);
    const onFocus = () => silent();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [activeId]);

  // ⌘/Ctrl+Z undoes the last action within the ~6s window
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        if (undoRef.current) { e.preventDefault(); runUndo(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const loadSent = useCallback(async () => {
    setSentLoading(true);
    try { const r = await api.get("/shares/sent"); setSent(r.data); }
    catch { /* noop */ }
    finally { setSentLoading(false); }
  }, []);

  useEffect(() => { if (tab === "sent") loadSent(); }, [tab, loadSent]);

  // tick so snoozed items return to the inbox automatically
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(id);
  }, []);

  // return nudge: ping when a snoozed code times out back into the inbox
  useEffect(() => {
    const prev = snoozedRef.current;
    const current = new Set();
    tasks.forEach((t) => { if (bucketOf(t, now) === "snoozed") current.add(t.id); });
    prev.forEach((id) => {
      if (!current.has(id)) {
        const t = tasks.find((x) => x.id === id);
        if (t && t.status !== "done" && t.snoozed_until && new Date(t.snoozed_until).getTime() <= now) {
          const mode = settings?.nudge || "toast";
          if (mode === "off") return;
          if (mode === "browser" && "Notification" in window && Notification.permission === "granted") {
            try { new Notification("🥕 Back in your field", { body: `${t.code} — ${t.title}` }); } catch {}
          } else {
            toast.info("Back in your field", { description: `${t.code} — ${t.title}`, duration: 8000 });
          }
        }
      }
    });
    snoozedRef.current = current;
  }, [tasks, now, settings]);

  // clear multi-select when switching tabs
  useEffect(() => { setSelected(new Set()); }, [tab]);

  // reset Done-tab pagination when the visible set changes
  useEffect(() => { setDoneVisible(50); }, [tab, query, categoryFilter, urgencyFilter, doneWindow]);

  const counts = useMemo(() => {
    const c = { inbox: 0, shared: 0, snoozed: 0, done: 0, sent: sent.length };
    tasks.forEach((t) => { c[bucketOf(t, now)] += 1; });
    return c;
  }, [tasks, now, sent]);

  const doneWindowCount = useMemo(
    () => tasks.filter((t) => t.status === "done" && inDoneWindow(t, doneWindow, now)).length,
    [tasks, doneWindow, now]
  );

  const snoozeWindowCount = useMemo(
    () => tasks.filter((t) => bucketOf(t, now) === "snoozed" && inSnoozeWindow(t, snoozeWindow, now)).length,
    [tasks, snoozeWindow, now]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = tasks.filter((t) => {
      if (bucketOf(t, now) !== tab) return false;
      if (tab === "done" && !inDoneWindow(t, doneWindow, now)) return false;
      if (tab === "snoozed" && !inSnoozeWindow(t, snoozeWindow, now)) return false;
      if (activeId) {
        if (assignFilter === "my" && t.assignee !== userId) return false;
        if (assignFilter === "unassigned" && t.assignee) return false;
      }
      if (categoryFilter !== "all") {
        if (categoryFilter === "none") { if (t.category) return false; }
        else if ((t.category || "") !== categoryFilter) return false;
      }
      if (urgencyFilter !== "all" && (t.priority || "").toLowerCase() !== urgencyFilter) return false;
      if (q) {
        const hay = `${t.code} ${t.title} ${t.notes}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const sorted = [...list];
    if (sortBy === "timeleft") {
      sorted.sort((a, b) => new Date(a.snoozed_until || 0).getTime() - new Date(b.snoozed_until || 0).getTime());
    } else if (sortBy === "priority") {
      sorted.sort((a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9));
    } else {
      sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return sorted;
  }, [tasks, query, sortBy, tab, now, categoryFilter, urgencyFilter, activeId, assignFilter, userId, doneWindow, snoozeWindow]);

  const openEdit = (t) => { setEditing(t); setModalOpen(true); };

  const focusGenerator = () => {
    document.getElementById("quick-generate")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.querySelector('[data-testid="qg-title-input"]')?.focus(), 350);
  };

  const act = async (task, url, patch, okMsg) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));
    try {
      const res = await api.post(url);
      setTasks((prev) => prev.map((t) => (t.id === task.id ? res.data : t)));
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error("Action failed");
      load();
    }
  };

  // ---- Undo (toast button + ⌘/Ctrl+Z, ~6s window) ----
  const registerUndo = (fn) => {
    const token = Date.now();
    undoRef.current = { fn, token };
    setTimeout(() => { if (undoRef.current?.token === token) undoRef.current = null; }, 6000);
  };
  const runUndo = async () => {
    const u = undoRef.current;
    if (!u) return;
    undoRef.current = null;
    try { await u.fn(); toast.success("Undone"); }
    catch { toast.error("Couldn't undo"); load(); }
  };
  const restore = async (task, prev) => {
    setTasks((p) => p.map((t) => (t.id === task.id ? { ...t, status: prev.status, snoozed_until: prev.snoozed_until } : t)));
    if (prev.status === "done") await api.post(`/tasks/${task.id}/complete`);
    else if (prev.snoozed_until) await api.post(`/tasks/${task.id}/snooze`, { until: prev.snoozed_until });
    else await api.post(`/tasks/${task.id}/reopen`);
  };
  const toastWithUndo = (msg, desc) =>
    toast.success(msg, { description: desc, duration: 6000, action: { label: "Undo ⌘Z", onClick: runUndo } });

  const onComplete = (t) => {
    const prev = { status: t.status, snoozed_until: t.snoozed_until };
    act(t, `/tasks/${t.id}/complete`, { status: "done", snoozed_until: null });
    registerUndo(() => restore(t, prev));
    toastWithUndo("Completed 🥕", t.code);
  };
  const onReopen = (t) => {
    const prev = { status: t.status, snoozed_until: t.snoozed_until };
    act(t, `/tasks/${t.id}/reopen`, { status: "todo", snoozed_until: null });
    registerUndo(() => restore(t, prev));
    toastWithUndo("Reopened", t.code);
  };
  const onAcceptShare = (t) => act(t, `/tasks/${t.id}/accept-share`, { share_status: null, share_note: null, status: "todo", snoozed_until: null }, "Added to your Field 🥕");
  const onDeclineShare = async (t) => {
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    try { await api.post(`/tasks/${t.id}/decline-share`); toast.success("Declined"); }
    catch { toast.error("Action failed"); load(); }
  };
  const onLeave = (t) => setLeaveTarget(t);
  const confirmLeave = async () => {
    const t = leaveTarget;
    setLeaveTarget(null);
    if (!t) return;
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    try { await api.post(`/tasks/${t.id}/leave`); toast.success("You left this shared root", { description: t.code }); }
    catch { toast.error("Could not leave"); load(); }
  };
  const onUnsnooze = (t) => {
    const prev = { status: "todo", snoozed_until: t.snoozed_until };
    act(t, `/tasks/${t.id}/unsnooze`, { snoozed_until: null });
    registerUndo(() => restore(t, prev));
    toastWithUndo("Back in your inbox", t.code);
  };

  const applySnooze = async (task, body, untilIso) => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const prev = { status: task.status, snoozed_until: task.snoozed_until };
    setTasks((prev2) => prev2.map((t) => (t.id === task.id ? { ...t, snoozed_until: untilIso, status: "todo" } : t)));
    try {
      const res = await api.post(`/tasks/${task.id}/snooze`, body);
      setTasks((prev2) => prev2.map((t) => (t.id === task.id ? res.data : t)));
      registerUndo(() => restore(task, prev));
      toastWithUndo("Snoozed", task.code);
    } catch (e) {
      toast.error("Snooze failed");
      load();
    }
  };

  const bumpSnooze = (key) => {
    const c = getSnoozeCounts();
    c[key] = (c[key] || 0) + 1;
    localStorage.setItem(SNOOZE_COUNTS_KEY, JSON.stringify(c));
    setFavKey(computeFav(c));
  };

  const onSnooze = (task, opt) => {
    bumpSnooze(opt.key);
    const dt = opt.resolve();
    return applySnooze(task, { until: dt.toISOString() }, dt.toISOString());
  };

  const onQuickSnooze = (task) => {
    const opt = snoozeOptions.find((o) => o.key === favKey) || snoozeOptions[0];
    if (!opt) return;
    onSnooze(task, opt);
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkComplete = async () => {
    const ids = [...selected];
    clearSelection();
    setTasks((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, status: "done", snoozed_until: null } : t)));
    try { await Promise.all(ids.map((id) => api.post(`/tasks/${id}/complete`))); toast.success(`Completed ${ids.length}`); load(); }
    catch { toast.error("Bulk action failed"); load(); }
  };
  const bulkSnooze = async (opt) => {
    const ids = [...selected];
    clearSelection();
    bumpSnooze(opt.key);
    const until = opt.resolve().toISOString();
    setTasks((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, snoozed_until: until, status: "todo" } : t)));
    try { await Promise.all(ids.map((id) => api.post(`/tasks/${id}/snooze`, { until }))); toast.success(`Snoozed ${ids.length}`); load(); }
    catch { toast.error("Bulk action failed"); load(); }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    clearSelection();
    setTasks((prev) => prev.filter((t) => !ids.includes(t.id)));
    try { await Promise.all(ids.map((id) => api.delete(`/tasks/${id}`))); toast.success(`Deleted ${ids.length}`); }
    catch { toast.error("Bulk action failed"); load(); }
  };

  const onCustomSnooze = (task) => {
    setCustomTarget(task);
    setCustomValue(tomorrow9());
  };

  const confirmCustomSnooze = () => {
    if (!customValue) return toast.error("Pick a date & time");
    const dt = new Date(customValue);
    if (isNaN(dt.getTime())) return toast.error("Invalid date & time");
    if (dt.getTime() <= Date.now()) return toast.error("Pick a time in the future");
    applySnooze(customTarget, { until: dt.toISOString() }, dt.toISOString());
    setCustomTarget(null);
  };

  const onSaveNotes = async (task, notes) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, notes } : t)));
    try {
      await api.put(`/tasks/${task.id}`, { notes });
      toast.success("Notes saved", { description: task.code });
    } catch (e) {
      toast.error("Could not save notes");
      load();
    }
  };

  const onShare = (task) => {
    setShareTarget(task);
    setShareEmails([]);
    setShareEmailInput("");
    setShareNote("");
    setShareLinked(true);
  };

  const addShareEmail = (raw) => {
    const e = (raw || "").trim().toLowerCase().replace(/,$/, "");
    if (!e) return;
    if (!e.includes("@")) return toast.error("That doesn't look like an email");
    if (shareEmails.includes(e)) { setShareEmailInput(""); return; }
    setShareEmails((prev) => [...prev, e]);
    setShareEmailInput("");
  };

  const confirmShare = async () => {
    const emails = [...shareEmails];
    const pending = shareEmailInput.trim().toLowerCase();
    if (pending && pending.includes("@") && !emails.includes(pending)) emails.push(pending);
    const note = shareNote.trim();
    if (emails.length === 0) return toast.error("Add at least one email");
    if (!note) return toast.error("Add a note so they know why you're sharing");
    setSharing(true);
    try {
      const res = await api.post(`/tasks/${shareTarget.id}/share`, { emails, note, linked: shareLinked });
      toast.success(res.data?.message || "Shared", { description: shareTarget.code });
      setShareTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally {
      setSharing(false);
    }
  };

  const onSetPriority = async (task, priority) => {
    if (task.priority === priority) return;
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, priority } : t)));
    try {
      await api.put(`/tasks/${task.id}`, { priority });
    } catch (e) {
      toast.error("Could not update priority");
      load();
    }
  };

  const confirmDelete = async () => {    const t = deleteTarget;
    setDeleteTarget(null);
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
      load();
    }
  };

  const tabs = [
    { key: "inbox", label: "Field", icon: Sprout },
    { key: "snoozed", label: "Snoozed", icon: Clock },
    { key: "done", label: "Done", icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-background grain">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="dashboard">
        <WelcomeCard />
        {!loading && <QuickGenerate onCreated={load} />}

        {/* Tabs */}
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap sm:flex-nowrap">
          <div className="inline-flex shrink-0 rounded-full border border-border bg-card p-0.5" data-testid="view-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                title={t.label}
                className={`inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  tab === t.key ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon className="w-3.5 h-3.5 shrink-0" />
                <span className={tab === t.key ? "inline" : "hidden sm:inline"}>{t.label}</span>
                <span className={`text-[11px] ${tab === t.key ? "text-white/80" : "text-muted-foreground"}`}>{t.key === "done" ? doneWindowCount : t.key === "snoozed" ? snoozeWindowCount : counts[t.key]}</span>
              </button>
            ))}
          </div>

          {tab !== "sent" && (
            <div className="flex items-center gap-2 min-w-0">
              {searchOpen || query ? (
                <div className="relative min-w-0 flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    ref={searchInputRef}
                    data-testid="task-search-input"
                    placeholder="Search…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onBlur={() => { if (!query) setSearchOpen(false); }}
                    autoFocus
                    className="pl-9 rounded-full w-full sm:w-[150px]"
                  />
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="icon"
                  data-testid="search-toggle-btn"
                  title="Search"
                  onClick={() => { setSearchOpen(true); requestAnimationFrame(() => searchInputRef.current?.focus()); }}
                  className="h-9 w-9 rounded-full shrink-0"
                >
                  <Search className="w-4 h-4" />
                </Button>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="filter-btn" className="h-9 w-9 rounded-full shrink-0 relative" title="Filter">
                    <SlidersHorizontal className="w-4 h-4" />
                    {(categoryFilter !== "all" || urgencyFilter !== "all") && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-background" data-testid="filter-active-dot" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 space-y-3" data-testid="filter-popover">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Urgency</Label>
                    <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                      <SelectTrigger data-testid="filter-urgency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All urgencies</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger data-testid="filter-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        <SelectItem value="none">No category</SelectItem>
                        {(settings?.categories || []).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="inline-flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${categoryDotCls(c.color)}`} /> {c.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(categoryFilter !== "all" || urgencyFilter !== "all") && (
                    <Button variant="ghost" size="sm" data-testid="filter-clear-btn" onClick={() => setFilters({ category: "all", urgency: "all" })} className="w-full text-muted-foreground">
                      Clear filters
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-select" className="w-[132px] shrink-0 rounded-full">
                  <ArrowUpDown className="w-3.5 h-3.5 mr-1 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tab === "snoozed" && <SelectItem value="timeleft" data-testid="sort-timeleft">Time left</SelectItem>}
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Workspace assignment filter */}
        {activeId && (
          <div className="inline-flex rounded-full border border-border bg-card p-0.5 mb-4" data-testid="assign-filter">
            {[
              { key: "my", label: "My roots" },
              { key: "all", label: "All roots" },
              { key: "unassigned", label: "Unassigned" },
            ].map((o) => (
              <button
                key={o.key}
                data-testid={`assign-filter-${o.key}`}
                onClick={() => setAssignFilter(o.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${assignFilter === o.key ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
        {/* Snoozed time-window filter (when items will repopulate) */}
        {tab === "snoozed" && (
          <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-0.5 mb-4" data-testid="snooze-window-filter">
            {SNOOZE_WINDOWS.map((o) => (
              <button
                key={o.key}
                data-testid={`snooze-window-${o.key}`}
                onClick={() => setSnoozeWindow(o.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${snoozeWindow === o.key ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}

        {/* Done time-window filter */}
        {tab === "done" && (
          <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-0.5 mb-4" data-testid="done-window-filter">
            {DONE_WINDOWS.map((o) => (
              <button
                key={o.key}
                data-testid={`done-window-${o.key}`}
                onClick={() => setDoneWindow(o.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${doneWindow === o.key ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}


        {/* Bulk triage bar */}
        {selected.size > 0 && (
          <div className="mb-3 flex items-center gap-2 flex-wrap rounded-xl border border-primary/30 bg-primary/5 px-3 py-2" data-testid="bulk-bar">
            <span className="text-sm font-semibold" data-testid="bulk-count">{selected.size} selected</span>
            <div className="ml-auto flex items-center gap-2">
              {tab !== "done" && (
                <Button size="sm" data-testid="bulk-complete" onClick={bulkComplete} className="h-8 rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Check className="w-3.5 h-3.5" /> Complete
                </Button>
              )}
              {tab === "inbox" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="bulk-snooze" className="h-8 rounded-full gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Snooze
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {snoozeOptions.map((o) => (
                      <DropdownMenuItem key={o.key} data-testid={`bulk-snooze-${o.key}`} onClick={() => bulkSnooze(o)} className="cursor-pointer">
                        <Clock className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> {o.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button size="sm" variant="ghost" data-testid="bulk-delete" onClick={bulkDelete} className="h-8 rounded-full gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </Button>
              <Button size="sm" variant="ghost" data-testid="bulk-clear" onClick={clearSelection} className="h-8 w-8 p-0 rounded-full">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-24"><Carrot className="w-8 h-8 text-primary animate-pulse" /></div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20 rounded-2xl border border-dashed border-border">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Carrot className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-base font-bold">
              {tab === "inbox" ? (query ? "No matches" : "Your field is clear 🎉") : tab === "shared" ? "Nothing shared with you" : tab === "snoozed" ? "Nothing snoozed" : "Nothing completed yet"}
            </h3>
            <p className="text-muted-foreground text-sm mt-1 mb-5">
              {tab === "inbox" && !query ? "No pending roots. Plant one below, or call it a day." : tab === "shared" && !query ? "Roots teammates send you will land here to accept." : "Try a different tab or search."}
            </p>
            {tab === "inbox" && !query && (
              <Button onClick={focusGenerator} data-testid="empty-create-btn" className="rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold">
                Plant a root
              </Button>
            )}
          </div>
        ) : (
          <>
            <TaskList
              tasks={tab === "done" ? filtered.slice(0, doneVisible) : filtered}
              bucket={tab}
              selected={selected}
              onToggleSelect={toggleSelect}
              onQuickSnooze={onQuickSnooze}
              favoriteLabel={(snoozeOptions.find((o) => o.key === favKey) || {}).label || "snooze"}
              snoozeOptions={snoozeOptions}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onComplete={onComplete}
              onSnooze={onSnooze}
              onCustomSnooze={onCustomSnooze}
              onUnsnooze={onUnsnooze}
              onReopen={onReopen}
              onSaveNotes={onSaveNotes}
              onSetPriority={onSetPriority}
              members={wsMembers}
              onAssign={onAssign}
            />
            {tab === "done" && filtered.length > doneVisible && (
              <div className="flex justify-center mt-4">
                <Button variant="outline" size="sm" data-testid="done-load-more" onClick={() => setDoneVisible((n) => n + 50)} className="rounded-full">
                  Load more ({filtered.length - doneVisible} more)
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      <TaskModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} task={editing} />

      <Dialog open={!!customTarget} onOpenChange={(o) => !o && setCustomTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="custom-snooze-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" /> Snooze until…
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-bold">{customTarget?.code}</span> will return to your field at this time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" data-testid="preset-tomorrow-9am" onClick={() => setCustomValue(tomorrow9())} className="rounded-full">
                Tomorrow 9 AM
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="preset-tonight" onClick={() => { const d = new Date(); d.setHours(18, 0, 0, 0); if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1); setCustomValue(toLocalInput(d)); }} className="rounded-full">
                This evening
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="preset-next-week" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); setCustomValue(toLocalInput(d)); }} className="rounded-full">
                Next week
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label>Exact date & time</Label>
              <Input
                type="datetime-local"
                data-testid="custom-snooze-input"
                value={customValue}
                min={toLocalInput(new Date())}
                onChange={(e) => setCustomValue(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCustomTarget(null)} data-testid="cancel-custom-snooze">Cancel</Button>
            <Button onClick={confirmCustomSnooze} data-testid="confirm-custom-snooze" className="bg-primary hover:bg-[#E85D04] text-white font-semibold rounded-lg">
              Snooze
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareTarget} onOpenChange={(o) => !o && setShareTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" /> Share this root
            </DialogTitle>
            <DialogDescription>
              Send <span className="font-mono font-bold">{shareTarget?.code}</span> to one or more teammates. It lands in their Field with the same root.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label>Their emails</Label>
            {shareEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pb-1" data-testid="share-email-chips">
                {shareEmails.map((e) => (
                  <span key={e} data-testid={`share-chip-${e}`} className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary rounded-full pl-2.5 pr-1 py-0.5">
                    {e}
                    <button onClick={() => setShareEmails((prev) => prev.filter((x) => x !== e))} data-testid={`share-chip-remove-${e}`} className="hover:bg-primary/20 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Input
              type="email"
              data-testid="share-email-input"
              placeholder="teammate@company.com — Enter to add another"
              value={shareEmailInput}
              onChange={(e) => setShareEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addShareEmail(shareEmailInput); } }}
              onBlur={() => { if (shareEmailInput.trim().includes("@")) addShareEmail(shareEmailInput); }}
              autoFocus
            />

            <div className="flex items-start gap-3 rounded-xl border border-border p-3 mt-2" data-testid="share-linked-row">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  {shareLinked ? <Link2 className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                  {shareLinked ? "Linked — stays in sync" : "Unlinked — send a copy"}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {shareLinked
                    ? "Completing, snoozing or editing syncs for everyone on this root."
                    : "Each person gets their own independent copy — changes don't sync."}
                </p>
              </div>
              <Switch checked={shareLinked} onCheckedChange={setShareLinked} data-testid="share-linked-toggle" />
            </div>

            <Label className="pt-2 block">Note <span className="text-muted-foreground font-normal">(why are you sharing this?)</span></Label>
            <Textarea
              data-testid="share-note-input"
              rows={3}
              placeholder="e.g. Can you take this one? The client is waiting on the banner proof."
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">If they're not on Carrot yet, it'll be waiting in their Shared tab when they sign in.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShareTarget(null)} data-testid="cancel-share-btn">Cancel</Button>
            <Button onClick={confirmShare} disabled={sharing} data-testid="confirm-share-btn" className="bg-primary hover:bg-[#E85D04] text-white font-semibold rounded-lg gap-1.5">
              <Send className="w-4 h-4" /> {sharing ? "Sending…" : "Share root"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this task?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono font-bold">{deleteTarget?.code}</span> — "{deleteTarget?.title}" will be removed permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!leaveTarget} onOpenChange={(o) => !o && setLeaveTarget(null)}>
        <AlertDialogContent data-testid="leave-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave this shared root?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono font-bold">{leaveTarget?.code}</span> will be removed from your Field. Everyone else keeps it — this only affects you.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-leave-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-leave-btn" onClick={confirmLeave} className="bg-primary hover:bg-[#E85D04] text-white">
              Leave root
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
